import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import ReactGA from "react-ga4";
import fetch from "../../auth/FetchInterceptor.js";
import { END_POINTS } from "constants/APIConstant.js";
import { APP_PREFIX_PATH } from "configs/AppConfig.js";
import { parseApiDateTime } from "utils/parseApiDateTime";
import { extractAxiosErrorMessage } from "utils/extractApiErrorMessage";
import { subscribeFormSchema } from "./subscribeSchema";
import { buildRegisterPayload, sortCountries } from "./utils";

export function useSubscribeForm() {
  const intl = useIntl();
  const navigate = useNavigate();
  const dir = useSelector(({ theme }) => theme.direction);
  const currentLocale = useSelector(({ theme }) => theme.locale);

  const {
    control,
    register,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState,
  } = useForm({
    resolver: yupResolver(subscribeFormSchema),
    defaultValues: {
      registrationType: "1",
    },
  });
  const { errors } = formState;

  const getCountries = useSWR({ url: END_POINTS.COUNTRY });
  const getSubscribtionType = useSWR({ url: END_POINTS.SUBSCRIPTION_TYPE });
  const getDepartment = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS_DEPARTMENT}=${END_POINTS.REGISTER_DEPARTMENT_ID}`,
  });
  const getServices = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS}/${END_POINTS.REGISTER_DEPARTMENT_ID}`,
  });

  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState("eg");

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
    ReactGA.send({
      hitType: "pageview",
      page: "/register",
      title: "Register Page",
    });
  }, []);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (getCountries?.data?.data != null) {
        setValue("country", 225);
      }
    }, 0);
    return () => clearTimeout(timeOut);
  }, [getCountries?.data, setValue]);

  const countries = useMemo(() => {
    if (
      getCountries.error ||
      getCountries.isLoading ||
      getCountries.data == null
    ) {
      return [];
    }
    return sortCountries(getCountries.data.data, dir);
  }, [getCountries.data, getCountries.error, getCountries.isLoading, dir]);

  const subscriptionTypes = useMemo(() => {
    if (
      getSubscribtionType.error ||
      getSubscribtionType.isLoading ||
      getSubscribtionType.data == null
    ) {
      return [];
    }
    return getSubscribtionType.data.data || [];
  }, [
    getSubscribtionType.data,
    getSubscribtionType.error,
    getSubscribtionType.isLoading,
  ]);

  const validateDobValue = (value) => {
    if (!value) return;

    const date = new Date(`${value}T00:00:00`);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (isNaN(date.getTime()) || date > now) {
      setError("dob", {
        type: "manual",
        message: intl.formatMessage({ id: "max-date-value" }),
      });
      return;
    }
    clearErrors("dob");
  };

  async function postLogin(data) {
    setButtonSpinner(true);

    if (!data.dob) {
      setShowMessage("تاريخ الميلاد مطلوب");
      setSuccess(false);
      setButtonSpinner(false);
      setShow(true);
      return;
    }

    const { normalizedMobile, localEgyptMobile, loginData } =
      buildRegisterPayload(data, selectedPhoneCountry);

    if (selectedPhoneCountry === "eg" && localEgyptMobile.length !== 11) {
      setShowMessage(intl.formatMessage({ id: "egypt-mobile-validation" }));
      setSuccess(false);
      setButtonSpinner(false);
      setShow(true);
      return;
    }

    if (!normalizedMobile.length) {
      setShowMessage(intl.formatMessage({ id: "mobile-required" }));
      setSuccess(false);
      setButtonSpinner(false);
      setShow(true);
      return;
    }

    try {
      const res = await fetch.post(END_POINTS.REGISTER, loginData);
      const responseData = res?.data ?? res;
      setShowMessage(
        responseData?.message ||
          intl.formatMessage({ id: "successfully-register" }),
      );
      setSuccess(true);
      setButtonSpinner(false);
      setShow(true);

      const userId = responseData?.id ?? responseData?.userId ?? null;
      const now = Date.now();
      const parsedResetTime = responseData?.resetTime
        ? parseApiDateTime(responseData.resetTime)
        : NaN;
      const effectiveResetTime =
        !Number.isNaN(parsedResetTime) && parsedResetTime > now
          ? new Date(parsedResetTime).toISOString()
          : new Date(now + 10 * 60 * 1000).toISOString();
      const verificationData =
        userId != null ? { userId, resetTime: effectiveResetTime } : null;

      if (verificationData && typeof sessionStorage !== "undefined") {
        localStorage.setItem(
          "emailVerificationCountDownTimer",
          effectiveResetTime,
        );
        sessionStorage.setItem(
          "emailVerificationData",
          JSON.stringify(verificationData),
        );
      }

      setTimeout(() => {
        setShow(false);
        if (verificationData) {
          navigate(`${APP_PREFIX_PATH}/verify-email`, {
            state: { verificationData },
          });
        } else {
          navigate(`${APP_PREFIX_PATH}/login`);
        }
      }, 1500);
    } catch (e) {
      console.error("Registration error:", e);
      setShowMessage(
        extractAxiosErrorMessage(
          e,
          intl.formatMessage({ id: "error-register" }),
        ),
      );
      setSuccess(false);
      setButtonSpinner(false);
      setShow(true);
    }
  }

  const onSubmit = handleSubmit((formData) => {
    const normalizedMobile = String(formData.mobile || "").replace(/\D/g, "");
    postLogin({ ...formData, mobile: normalizedMobile });
  });

  return {
    dir,
    currentLocale,
    control,
    register,
    errors,
    setError,
    clearErrors,
    onSubmit,
    buttonSpinner,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    selectedPhoneCountry,
    setSelectedPhoneCountry,
    validateDobValue,
    countries,
    subscriptionTypes,
    getDepartment,
    getServices,
    isLoading: getDepartment.isLoading || getServices.isLoading,
    snackbar: {
      open: show,
      onClose: () => setShow(false),
      severity: success ? "success" : "error",
      message: showMessage,
    },
  };
}
